<template>
    <Dialog
        v-model:visible="dialogOpen"
        closable
        close-on-escape
        dismissableMask
        modal
        position="center"
        :pt="{
            root: 'p-0 xs:w-full max-w-2xl rounded-t-lg overflow-hidden',
            content: 'px-0 overflow-hidden ',
            header: 'h-0 p-1',
        }"
        :pt-options="{ mergeProps: true }"
        :showHeader="false"
    >
        <RequestDocument
            :clients="clients"
            :file-id="fileId"
            @close="closeModal"
            @send-request="handleSendRequest"
        />
    </Dialog>
</template>

<script lang="ts" setup>
import { notifyError } from '@/bugsnag';
import type { Client, File, SendRequestPayload } from '@/company-portal/view-file/RequestDocument.types';
import { BRIDGE_EVENT_EMITTER, BRIDGE_EVENTS } from '@/shared/constants/event-emitter';
import { useFetch } from '@vueuse/core';
import toastr from 'toastr';

import type Emittery from 'emittery';
import Dialog from 'primevue/dialog';
import { inject, ref } from 'vue';
import RequestDocument from './RequestDocument.vue';

const dialogOpen = ref(false);
const bridgeEventEmitter = inject<Emittery>(BRIDGE_EVENT_EMITTER);

const fileId = ref(0);
const clients = ref<Client[]>([]);
let currentUserRole: string;
let currentCompany: string;
let currentAccount: string;
let currentBranchName: string;
let currentTPS: string;

bridgeEventEmitter?.on(
    BRIDGE_EVENTS.REQUEST_DOCUMENT.CHANGE_REQUEST_DOCUMENT_FILE_ID,

    async ({
        clients: newClients,
        file: file,
        userRole: userRole,
        account: account,
    }: {
        clients: Client[];
        file: File;
        userRole: string;
        account: string;
    }) => {
        clients.value = newClients;
        fileId.value = file.id;
        currentUserRole = userRole;
        currentCompany = file.title_name;
        currentAccount = account;
        currentBranchName = file.branch_name;
        currentTPS = file.vendor || 'none';
        dialogOpen.value = true;
    },
);

function closeModal() {
    dialogOpen.value = false;
}

async function handleSendRequest({ selectedClients, documents }: SendRequestPayload) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    if (!csrfToken) {
        console.error('CSRF token not found');
        return;
    }

    // amplitude events for each client in selectedClients and each document in documents
    if (window.amplitude) {
        selectedClients.forEach(client => {
            documents.forEach(document => {
                window.amplitude.track('file action', {
                    'portal type': 'company',
                    role: currentUserRole,
                    fileId: fileId.value,
                    action: 'request document',
                    client: client.email,
                    'client type': client.role,
                    'requested document name': document,
                    account: currentAccount,
                    company: currentCompany,
                    branch: currentBranchName,
                    tps: currentTPS,
                });
            });
        });
    }

    const payload = {
        _token: csrfToken,
        fileId: fileId.value,
        selectedClients,
        client_email_list: selectedClients.map(client => client.email),
        requested_file_names: documents,
    };

    const createDocumentTaskEndpoint = '/user/document-tasks/' + fileId.value;

    try {
        await useFetch(createDocumentTaskEndpoint).post(payload);
        toastr.success('A document request has been sent.', 'Success!', { timeOut: 5000 });
    } catch (error) {
        toastr.error('Please try again or refresh the page.', 'Error!', { timeOut: 5000 });
        notifyError(error, 'RequestDocumentWrapper:handleSendRequest', { payload });
    } finally {
        closeModal();
        window.dispatchEvent(new CustomEvent('refresh-buyer-table'));
    }
}
</script>
